import { AppForm } from '../../../../../../../common/forms/app.form';
import { Injectable, Signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  ApplicationAppEntityApplicationRead,
  ApplicationAppEntityApplicationWrite,
  ParcelAppEntityParcelWrite
} from '../../../../../../../openapi';
import {
  DictTownRoadItem, DictTownStreetItem,
  DictTownWithRoadsItem,
  DictTownWithStreetsItem
} from '../../../../../../../services/dictionary.common.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { DictionaryCommonService } from '../../../../../../../services/dictionary.common.service';

@Injectable()
export class NotificationGeneralRemarksRoadMarkingLocalisationForm extends AppForm {

  public townWithStreetsList: Signal<DictTownWithStreetsItem[] | undefined> = toSignal(this.dictService.getTownWithStreetsList());

  public townWithRoadsList: Signal<DictTownWithRoadsItem[] | undefined> = toSignal(this.dictService.getTownWithRoadsList());

  public getRoadList(townName?: string | null): Array<DictTownRoadItem> {
    return this.townWithRoadsList()?.filter(v => v.name === townName).at(0)?.roads || [];
  }

  public getStreetList(townName?: string | null): Array<DictTownStreetItem> {
    return this.townWithStreetsList()?.filter(v => v.name === townName)?.at(0)?.streets || [];
  }

  constructor(private dictService: DictionaryCommonService) {
    super({
      geometry: new FormControl(null),
      provideRoadNumberAndName: new FormControl(false, { nonNullable: true }),
      city: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }),
      parcels: new FormControl<ParcelAppEntityParcelWrite[]>(
        [], {
          nonNullable: true,
          validators: [Validators.maxLength(255)]
        }
      )
    });
  }

  public get provideRoadNumberAndName(): FormControl {
    return this.get('provideRoadNumberAndName') as FormControl;
  }

  public get city(): FormControl {
    return this.get('city') as FormControl;
  }

  public cityChange(v?: string | null): void {
    this.street?.setValue('');
    this.street?.markAsUntouched();
    this.roadNumber?.setValue('');
    this.roadNumber?.markAsUntouched();
  }

  public provideRoadNumberAndNameChange(v?: boolean): void {
    this.city.setValue('');
    this.city.markAsUntouched();
    if (v) {
      this.addControl('roadNumber', new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }));

      this.addControl('roadName', new FormControl({
        disabled: true,
        value: null
      }, [Validators.required, Validators.maxLength(255)]));

      this.removeControl('street');
      this.removeControl('houseNumber');
    } else {
      this.removeControl('roadName');
      this.removeControl('roadNumber');
      this.addControl('street', new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }));
      this.addControl('houseNumber', new FormControl(null, [Validators.maxLength(255)]));
    }
  }

  public get roadName(): FormControl | null {
    return this.get('roadName') as FormControl | null;
  }

  public get roadNumber(): FormControl | null {
    return this.get('roadNumber') as FormControl | null;
  }

  public roadNumberChange(roadNumber: string): void {
    const roadName = this.getRoadList(this.city.value).filter(v => v.roadNumber === roadNumber).at(0)?.roadName || '';
    this.roadName?.setValue(roadName);
  }

  public get houseNumber(): FormControl | null {
    return this.get('houseNumber') as FormControl | null;
  }

  public get street(): FormControl | null {
    return this.get('street') as FormControl | null;
  }

  public get geometry(): FormControl {
    return this.get('geometry') as FormControl;
  }

  public toWriteModel(): ApplicationAppEntityApplicationWrite {
    let value = { ...this.value, geometry: JSON.stringify(this.geometry.value) };
    delete value.parcels;
    return {
      ...value,
      provideRoadNumberAndName: this.value.provideRoadNumberAndName,
      roadName: this.roadName?.value || null,
      roadNumber: this.value?.roadNumber || null,
      city: this.value?.city,
      street: this.street?.value || null,
      houseNumber: this.houseNumber?.value || null,
    };
  }

  public override patchFormValues(app: ApplicationAppEntityApplicationRead) {
    this.provideRoadNumberAndNameChange(app?.provideRoadNumberAndName);
    super.patchFormValues(app);
  }

  public get parcels(): FormControl {
    return this.get('parcels') as FormControl;
  }
}
